import debug from "@cher-ami/debug"
import gsap from "gsap"
import PostHand from "~/components/postHand/postHand"
import PostNavigationStars from "~/components/postNavigationStars/PostNavigationStars"
import PostSlider from "~/components/postSlider/PostSlider"
import PostSliderButton from "~/components/postSliderButton/PostSliderButton"
import {
	defaultPlayInTranslate,
	defaultPlayOut,
	defaultPlayOutTranslate
} from "~/helpers/defaultTransitions"
import { isMobile } from "~/helpers/isMobile"
import { Component } from "~/libs/compose"
import postsNavigationService from "~/services/PostsNavigationService"

const log = debug(`front:SinglePostPage`)

type TStaticProps = {}

/**
 * @name SinglePostPage
 */
export default class SinglePostPage extends Component<TStaticProps> {
	public static attrName = "SinglePostPage"

	private isPostTransiting: boolean = false

	// scroll properties
	private touchStartY = 0
	private homeUrl: string = "/"
	canOpenHome: boolean = false
	canOpenHomeTimeout: null | ReturnType<typeof setTimeout> = null
	preScrollIntroTimeout: null | ReturnType<typeof setTimeout> = null

	// ----------------------------------------------------------------------------- COMPONENTS

	public slider = this.add(PostSlider)
	public hand = this.add(PostHand)
	public nav = this.add(PostNavigationStars)
	public buttons = this.addAll(PostSliderButton) as PostSliderButton[]
	touchStartX: number

	// ----------------------------------------------------------------------------- LIFECYCLE

	public mounted() {
		log("> mounted")
		log("this.slider", this.slider)
		window.addEventListener("resize", this.resizeHandler)
		this.slider.hand = this.hand

		this.attachEvents()

		// NOTE : Mandatory to use post navigation service
		// set total posts and current index
		postsNavigationService.totalPosts = this.slider.slides.length
		postsNavigationService.currentIndex = this.slider.currentSlideIndex
	}

	public unmounted() {
		window.removeEventListener("resize", this.resizeHandler)
		this.slider.unmounted()

		this.detachEvents()
	}

	protected resizeHandler = () => {}

	// ----------------------------------------------------------------------------- EVENTS

	attachEvents() {
		postsNavigationService.on("postchange", this.onPostChange)
		this.nav.emitter.on("nav:change", this.handleNavChange)
		this.buttons.forEach((button) => button.emitter.on("click", this.onSliderButtonClick))

		// cross browser wheel event
		window.addEventListener("wheel", this.wheelHandler)
		window.addEventListener("mousewheel", this.wheelHandler)
		// mobile touchmove event
		window.addEventListener("touchstart", this.touchStartHandler)
		window.addEventListener("touchmove", this.wheelHandler)
	}

	detachEvents() {
		postsNavigationService.off("postchange", this.onPostChange)
		this.nav.emitter.off("nav:change", this.handleNavChange)
		this.buttons.forEach((button) =>
			button.emitter.off("click", this.onSliderButtonClick)
		)

		// cross browser wheel event
		window.removeEventListener("wheel", this.wheelHandler)
		window.removeEventListener("mousewheel", this.wheelHandler)
		// mobile touchmove event
		window.removeEventListener("touchstart", this.touchStartHandler)
		window.removeEventListener("touchmove", this.wheelHandler)

		this.resetPreScrollIntro()
	}

	// --------------------------------------------------------------------------- SCROLL TOP GO BACK TO HOME

	protected touchStartHandler = (e: TouchEvent) => {
		this.touchStartY = e.touches[0].pageY
		this.touchStartX = e.touches[0].pageX
	}

	protected wheelHandler = (e: WheelEvent | TouchEvent) => {
		// open first story if deltaY > 8 and we are at the bottom of the page
		const scrollDelta = (e as WheelEvent)?.deltaY
		const touchDelta = this.touchStartY - (e as TouchEvent)?.touches?.[0]?.pageY
		const deltaY = scrollDelta || touchDelta
		const tresholdY = e.type === "touchmove" ? 16 : 12

		// touch translation
		const touchDeltaX = this.touchStartX - (e as TouchEvent)?.touches?.[0]?.pageX
		if (Math.abs(touchDeltaX) > 10) {
			return
		}

		// if we are at the top of the page
		if (window.scrollY === 0) {
			// allow to open first story only if we are at the bottom of the page and after a timeout
			this.canOpenHomeTimeout = setTimeout(() => {
				this.canOpenHome = true
			}, 100)

			// allow to open directly the first story on desktop
			if (!isMobile()) {
				this.canOpenHome = true
			}

			// open first story if deltaY > tresholdY
			if (this.canOpenHome) {
				if (deltaY < -tresholdY) {
					this.stack.handleHistory(this.homeUrl)
				}

				this.preScrollIntro(deltaY, tresholdY)
			}
		} else {
			this.canOpenHome = false
			clearTimeout(this.canOpenHomeTimeout)
		}
	}

	preScrollIntro(deltaY: number, treshold: number) {
		this.$root.style.transform = `translateY(${-deltaY / (isMobile() ? 1.5 : 2)}px)`

		this.preScrollIntroTimeout = setTimeout(() => {
			if (Math.abs(deltaY) < treshold * 1) {
				this.$root.style.transform = `translateY(0)`
			}
		}, 200)
	}

	resetPreScrollIntro() {
		this.$root.style.transform = `translateY(0)`
	}

	// --------------------------------------------------------------------------- CONTROL SLIDER

	private onPostChange = async (index: number) => {
		if (this.isPostTransiting) return
		this.isPostTransiting = true
		this.nav.update(index)
		await this.slider.moveTo(index)
		this.isPostTransiting = false
	}

	private handleNavChange = ({ index }: { index: number }) => {
		postsNavigationService.updateCurrentPost(index)
	}

	private onSliderButtonClick = ({
		event,
		direction
	}: {
		event: Event
		direction: number
	}) => {
		if (this.isPostTransiting) return
		// get target index in loop from direction
		let targetIndex
		const currentIndex = postsNavigationService.currentIndex
		const totalPosts = postsNavigationService.totalPosts
		targetIndex =
			direction === 1
				? (currentIndex + 1) % totalPosts
				: (currentIndex - 1 + totalPosts) % totalPosts

		postsNavigationService.updateCurrentPost(targetIndex)
	}

	// --------------------------------------------------------------------------- PAGE TRANSITION

	public playOut(goTo: string, resolve: () => void) {
		log("> playOut to go to", goTo)
		// hide buttons
		this.buttons.forEach((button) => button.playOut())

		if (goTo === "HomePage") {
			// translate this.hand up
			this.hand.$root.animate([{ transform: `translateY(${-window.innerHeight}px)` }], {
				duration: 0.5 * 1000,
				easing: "ease-out"
			})
			this.hand.playOut()
			gsap.delayedCall(0, () => {
				defaultPlayOutTranslate(this.$root, goTo, resolve, "down")
			})
		} else {
			defaultPlayOut(this.$root, goTo, resolve)
		}
	}

	public playIn(comeFrom: string, resolve: () => void) {
		log("> playIn", { buttons: this.buttons })

		// reveal nav
		this.nav.playIn(1200)

		// reveal buttons
		this.buttons.forEach((button) => button.playIn(1.3))

		gsap.delayedCall(0.04, () => {
			this.$root.style.opacity = "1"
			defaultPlayInTranslate(this.$root, comeFrom, resolve, "down", 0.6)
		})
		// Trigger nav change to playIn slider with delay and startIndex
		gsap.delayedCall(0.25, () => {
			this.handleNavChange({ index: +this.slider.$root.dataset.startIndex })
		})
	}
}
